import type { InheritanceReuseCandidateEntity } from "@/features/inheritanceReuse/types";

// /acquirebook（AcquireBookFormPage）が解釈するクエリパラメータ:
//   prefecture  … 都道府県名（getPrefectureIdByValue で ID 解決）
//   city        … 市区町村「名」（useCityWithExternalState が getCityCodeByCityName で
//                 コードへ解決し市区町村セレクトを自動選択する。initialCityName）
//   location    … 市区町村を除いた所在（locationWithoutCity）
//   chiban      … 地番・家屋番号（1 要素のみ対応）
//   realEstate  … 不動産種別の英字コード（LAND/BUILDING/CONDOMINIUM/ONE_BUILDING）
//
// 候補 API は市区町村「名」（cityName）を返す。/acquirebook 側は名称→コード解決に対応しているため、
//   - city には cityName を渡して市区町村を自動選択させる
//   - location には都道府県名・市区町村名を除いた所在を渡す（自動選択後の所在二重化を防ぐ）
// とする（例: 「千代田区千代田区…」の重複を回避）。

/** /acquirebook が受け付ける不動産種別コード。 */
const KNOWN_REAL_ESTATE_TYPES = new Set([
  "LAND",
  "BUILDING",
  "CONDOMINIUM",
  "ONE_BUILDING",
]);

// 日本語表記で来た場合の英字コードへの対応（防御的変換）。
const JPN_TO_ENG: Record<string, string> = {
  土地: "LAND",
  建物: "BUILDING",
  区分建物: "CONDOMINIUM",
  一棟: "ONE_BUILDING",
};

/** 候補の realEstateType を /acquirebook が解釈できる英字コードへ正規化する。不明なら undefined。 */
export const normalizeRealEstateType = (
  realEstateType: string | undefined | null
): string | undefined => {
  if (realEstateType == null) return undefined;
  const value = realEstateType.trim();
  if (value === "") return undefined;
  const upper = value.toUpperCase();
  if (KNOWN_REAL_ESTATE_TYPES.has(upper)) return upper;
  return JPN_TO_ENG[value];
};

/**
 * 所在（location）から、先頭に付く都道府県名・市区町村名を取り除いて
 * 「市区町村を除いた所在（locationWithoutCity）」を得る。
 * /acquirebook 側で市区町村を選択した際に所在が二重にならないようにするための前処理。
 * @param location 所在（市区町村・場合により都道府県を含む）
 * @param prefectureName 都道府県名
 * @param cityName 市区町村名（無い場合は除去しない）
 */
export const stripLocationPrefixes = (
  location: string,
  prefectureName: string,
  cityName: string | null | undefined
): string => {
  let result = location.trim();
  const prefecture = prefectureName.trim();
  if (prefecture !== "" && result.startsWith(prefecture)) {
    result = result.slice(prefecture.length);
  }
  const city = (cityName ?? "").trim();
  if (city !== "" && result.startsWith(city)) {
    result = result.slice(city.length);
  }
  return result.trim();
};

/**
 * 所在（location）から先頭の都道府県名のみを取り除く（市区町村は残す）。
 * 一括取得（/acquiremultiplebooks）の locationFull は都道府県を別項目(prefName)で持ち、
 * 所在は市区町村を含む形で扱うため、都道府県のみ除去する。
 */
export const stripPrefecturePrefix = (
  location: string,
  prefectureName: string
): string => {
  const result = location.trim();
  const prefecture = prefectureName.trim();
  return prefecture !== "" && result.startsWith(prefecture)
    ? result.slice(prefecture.length).trim()
    : result;
};

/**
 * 「リユース取得」で遷移する /acquirebook の相対 URL（クエリ付き）を組み立てる。
 * 都道府県・市区町村（名）・所在（都道府県名・市区町村名を除いた部分）・地番・種別を引き継ぐ。
 * 値が無い項目は付けない。city は名称で渡し、遷移先が名称→コード解決で自動選択する。
 */
export const buildAcquireUrl = (
  candidate: Pick<
    InheritanceReuseCandidateEntity,
    | "prefectureName"
    | "location"
    | "cityName"
    | "chibanKaokuNumber"
    | "realEstateType"
  >
): string => {
  const params = new URLSearchParams();
  if (candidate.prefectureName.trim() !== "") {
    params.set("prefecture", candidate.prefectureName.trim());
  }
  const cityName = (candidate.cityName ?? "").trim();
  if (cityName !== "") {
    params.set("city", cityName);
  }
  const locationWithoutCity = stripLocationPrefixes(
    candidate.location,
    candidate.prefectureName,
    candidate.cityName
  );
  if (locationWithoutCity !== "") {
    params.set("location", locationWithoutCity);
  }
  if (candidate.chibanKaokuNumber.trim() !== "") {
    params.set("chiban", candidate.chibanKaokuNumber.trim());
  }
  const realEstate = normalizeRealEstateType(candidate.realEstateType);
  if (realEstate !== undefined) {
    params.set("realEstate", realEstate);
  }
  const query = params.toString();
  return query === "" ? "/acquirebook" : `/acquirebook?${query}`;
};
